import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { toast } from "react-toastify";
import TableComponent from "../../components/Table";
import ConfirmDialog from "../../components/ConfirmDialog";
import theme from "../../theme/theme";
import {
  fetchItemCategories,
  createItemCategory,
  updateItemCategory,
  deleteItemCategory,
} from "../../api/InventoryService";

const ItemCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [links, setLinks] = useState([]);
  const [meta, setMeta] = useState({ from: 0, to: 0, total: 0 });

  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ id: null, name: "" });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchItemCategories({ page: currentPage, per_page: perPage });
      setCategories(res.data.data || []);
      setLinks(res.data.links || []);
      setMeta({ from: res.data.from, to: res.data.to, total: res.data.total });
    } catch (error) {
      toast.error("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [perPage]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleOpenDialog = (category = null) => {
    if (category) {
      setFormData({ id: category.id, name: category.name });
    } else {
      setFormData({ id: null, name: "" });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return toast.warning("Name is required");
    setSaving(true);
    try {
      if (formData.id) {
        await updateItemCategory(formData.id, { name: formData.name });
        toast.success("Category updated");
      } else {
        await createItemCategory({ name: formData.name });
        toast.success("Category created");
      }
      setOpenDialog(false);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (category) => {
    setDeleteTarget(category);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteItemCategory(deleteTarget.id);
      toast.success("Category deleted");
      setConfirmOpen(false);
      setDeleteTarget(null);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete category");
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID" },
    { field: "name", headerName: "Category Name" },
  ];

  const actions = [
    {
      icon: <Edit />,
      label: "Edit",
      onClick: (row) => handleOpenDialog(row),
      backgroundColor: theme.button.editBackgroundColor,
    },
    {
      icon: <Delete />,
      label: "Delete",
      onClick: (row) => handleDeleteClick(row),
      backgroundColor: theme.button.deleteBackgroundColor,
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Box component="h1" sx={{ m: 0 }}>
          Item Categories
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: theme.button.borderRadius }}
        >
          Add Category
        </Button>
      </Box>

      <TableComponent
        columns={columns}
        data={categories}
        actions={actions}
        loading={loading}
        links={links}
        setCurrentPage={setCurrentPage}
        meta={meta}
        perPage={perPage}
        setPerPage={setPerPage}
      />

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {formData.id ? "Edit Category" : "Add Category"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Category Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Category"
        message={`Are you sure you want to delete category "${deleteTarget?.name}"?`}
        loading={deleting}
      />
    </Box>
  );
};

export default ItemCategoriesPage;
